<script>
import { userStatusFormatter, arrayToSentence } from '@utils/string-helpers'
import ProfilePicture from '@components/profile-picture.vue'
import Tooltip from '@components/tooltip.vue'
import { formatDate } from '@utils/date-helpers'
import { authComputed } from '@state/helpers'
import design from '@design'

export default {
  components: {
    ProfilePicture,
    Tooltip,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
    },
  },
  computed: {
    ...authComputed,
    tooltipMessage() {
      if (this.user.isDeactivationPending) {
        return 'Deactivation request is under review'
      }

      if (this.user.status === 'accessible_termination' && this.user.secondaryEmail) {
        return `You have thirty days beginning ${formatDate(
          this.user.deactivateDate,
          'YYYY-MM-DD',
          'MM/DD/yyyy'
        )} to access this user’s email with the following email address ${this.user.primaryEmail}`
      }

      return ''
    },

    tooltipMLOMessage() {
      if (this.user.loReviewPending) {
        return `LO Phase 2 request has been submitted and is currently under review.`
      }

      if (this.user.isLicenseChangePending) {
        return `State License request has been submitted and is under review.`
      }

      return ''
    },
    getStatusTextColor() {
      const status = userStatusFormatter(this.user.status)
      if (status === 'Active') {
        return design['secondary-green']
      }

      if (status === 'Pending Activation' || status === 'Email Access' || status === 'Pending Deactivation') {
        return design['secondary-orange']
      }

      return design.gray
    },
  },
  methods: {
    userStatusFormatter,
    arrayToSentence,
    isUserMe({ id }) {
      return this.currentUser.id === id
    },
    isUserInteractable(user) {
      return (
        !this.readOnly &&
        user.status !== 'inactive' &&
        user.status !== 'accessible_termination' &&
        !user.groupLabels.includes('Record Only')
      )
    },
    isDisabledDeactivateButton(user) {
      if (user.isDeactivationPending && (user.isLoPhaseTwo || user.isLoanProcessor)) {
        return true
      }

      return user.isBrokerOwner
    },

    viewProfile(openNewTab, user) {
      this.$emit('view-profile', user, openNewTab)
    },
  },
}
</script>

<template>
  <div :class="[$style.officeRosterUser]">
    <div class="d-flex">
      <ProfilePicture :class="$style.profilePicture" :src="user.photoUrl" @click.native="viewProfile(true, user)" />
      <div :class="$style.contentContainer">
        <p
          :class="[$style.userStatusText, tooltipMessage ? $style.userStatusTextWithTooltip : '']"
          class="tag-text"
          :style="{ color: getStatusTextColor }">
          {{ userStatusFormatter(user.status) }}
        </p>
        <div>
          <p :class="$style.name" class="button-text color-primary-blue">{{ user.firstName }} {{ user.lastName }}</p>
          <Tooltip
            v-if="tooltipMessage"
            :class="$style.pendingTooltip"
            :target="`${user.id}tooltip`"
            placement="top"
            theme="orange"
            icon-color="orange">
            {{ tooltipMessage }}
          </Tooltip>
        </div>
        <div>
          <div v-for="label in _.take(user.groupLabels, 3)" :key="label" class="position-relative width-fit-content">
            <p class="overline-text color-primary-blue">
              {{ label }}
              <Tooltip
                v-if="tooltipMLOMessage && label === 'MLO Phase 2 (pending)'"
                :target="`${user.id}tooltip-mlo`"
                class="ml-1"
                placement="top"
                theme="orange"
                icon-color="orange"
                :icon-absolute-position="false">
                {{ tooltipMLOMessage }}
              </Tooltip>
            </p>
          </div>
          <template v-if="user.groupLabels.length === 4">
            <p class="overline-text color-primary-blue">{{ user.groupLabels[3] }}</p>
          </template>
          <template v-else-if="user.groupLabels.length > 4">
            <p
              :id="`show-more-roles-tooltip-user-pill-${user.id}`"
              class="width-fit-content overline-text color-primary-blue">
              + {{ user.groupLabels.length - 3 }} more
            </p>
            <b-tooltip
              :target="`show-more-roles-tooltip-user-pill-${user.id}`"
              :placement="`bottom`"
              custom-class="tooltipBlue"
              no-fade>
              <p v-for="label in _.slice(user.groupLabels, 3)" :key="label" class="color-white text-center">
                {{ label }}
              </p>
            </b-tooltip>
          </template>
        </div>
      </div>
    </div>
    <div :class="$style.buttonContainer">
      <BaseButton
        :style="{ visibility: !isUserMe(user) ? 'visible' : 'hidden' }"
        theme="blue"
        :options="{ defaultPadding: false }"
        @click.prevent="viewProfile(false, user)"
        @mousedown.middle.prevent="viewProfile(true, user)">
        View Profile
      </BaseButton>
      <BaseButton
        theme="blue"
        :options="{ defaultPadding: false }"
        :disabled="user.isAccessOnly || user.isHQUser || !isUserInteractable(user)"
        @click="$emit('update-role', user)">
        Update Role
      </BaseButton>
      <BaseButton
        theme="blue"
        :options="{ defaultPadding: false }"
        :disabled="!isUserInteractable(user) || isUserMe(user) || isDisabledDeactivateButton(user)"
        @click="$emit('deactivate-user', user)">
        Deactivate
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.profilePicture {
  @media (max-width: $min-width-breakpoint + 20px) {
    margin-left: 0 !important;
  }

  @media (min-width: $min-width-breakpoint + 20px) and (max-width: $sm-breakpoint - 100px) {
    margin-left: 0 !important;
  }

  flex-shrink: 0;
  width: 110px;
  height: 110px;
  margin: 10px 0 0 19px;
  cursor: pointer;
}

.contentContainer {
  width: 100%;
  padding: 6px 10px 6px 15px;
}

.buttonContainer {
  @media (max-width: $min-width-breakpoint + 20px) {
    flex-wrap: wrap;
    margin: 20px 0 0;

    button {
      padding: 4px !important;
      font-size: 12px;
    }
  }

  @media (min-width: $min-width-breakpoint + 20px) and (max-width: $sm-breakpoint - 100px) {
    flex-wrap: wrap;
    margin: 20px 0 0;

    button {
      padding: 6px !important;
      font-size: 12px;
    }
  }

  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  margin: 20px 20px 0;

  button {
    height: 40px;
    padding: 12px;
  }
}

.officeRosterUser {
  @media (max-width: $lg-breakpoint) {
    min-width: unset;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 360px;
  padding: 6px;
  background-color: $lightest-gray;
  border: $primary-slate 2px solid;
  border-radius: 16px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.9);
}

.name {
  padding-right: 30px;
  word-break: break-word;
}

.userStatusText {
  margin-right: 10px;
  text-align: right;
}

.userStatusTextWithTooltip {
  margin-right: 30px;
}

.pendingTooltip {
  > div {
    top: 8px !important;
  }
}
</style>
